import React, { useState, useEffect } from "react";
import "./AartiList.css";
import AddAartiForm from "./AddAartiForm";
import apiClient from "../../api";

const AartiList = () => {
  const [aartis, setAartis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingAarti, setEditingAarti] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", description: "", content: "" });

  useEffect(() => {
    fetchAartis();
  }, []);

  const fetchAartis = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/api/aarti/list/");
      const data = response.data;
      if (Array.isArray(data)) {
        setAartis(data);
      } else if (data && Array.isArray(data.results)) {
        setAartis(data.results);
      } else {
        setAartis([]);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching aartis:", err);
      setError("Failed to load aartis");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (aartiId) => {
    if (!window.confirm("Are you sure you want to delete this aarti?")) return;

    try {
      await apiClient.delete(`/api/aarti/delete/${aartiId}/`);
      alert("Aarti deleted successfully");
      await fetchAartis();
    } catch (err) {
      console.error("Error deleting aarti:", err);
      alert("Error deleting aarti");
    }
  };

  const handleSave = async (formData) => {
    try {
      await apiClient.post("/api/aarti/add/", formData);
      setShowAddForm(false);
      alert("Aarti created successfully");
      await fetchAartis();
    } catch (err) {
      console.error("Error saving aarti:", err);
      alert("Error saving aarti");
    }
  };

  const handleEditClick = (aarti) => {
    setEditingAarti(aarti);
    setEditFormData({
      name: aarti.name || "",
      description: aarti.description || "",
      content: aarti.content || ""
    });
  };

  const handleEditCancel = () => {
    setEditingAarti(null);
    setEditFormData({ name: "", description: "", content: "" });
  };

  const handleEditSave = async () => {
    try {
      await apiClient.put(`/api/aarti/update/${editingAarti.id}/`, editFormData);
      alert("Aarti updated successfully");
      setEditingAarti(null);
      setEditFormData({ name: "", description: "", content: "" });
      await fetchAartis();
    } catch (err) {
      console.error("Error updating aarti:", err);
      alert("Error updating aarti");
    }
  };

  const filteredAartis = aartis.filter(
    (aarti) =>
      aarti.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aarti.description &&
        aarti.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (showAddForm) {
    return (
      <AddAartiForm
        onClose={() => setShowAddForm(false)}
        onSave={handleSave}
      />
    );
  }

  if (loading) return <div className="loading-message">Loading aartis...</div>;

  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="aarti-list">
      {/* Edit Modal */}
      {editingAarti && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <h2>✏️ Edit Aarti</h2>
            <div className="edit-form">
              <label>Name:</label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
              <label>Description:</label>
              <textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
              />
              <label>Content:</label>
              <textarea
                value={editFormData.content}
                onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
                rows={8}
              />
              <div className="edit-modal-buttons">
                <button className="save-button" onClick={handleEditSave}>💾 Save</button>
                <button className="cancel-button" onClick={handleEditCancel}>❌ Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="aarti-header">
        <h1 className="aarti-title">🕯️ Aarti Management</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="add-aarti-button"
        >
          ➕ Add New Aarti
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="🔍 Search aartis by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredAartis.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: "#f8f9fa",
            borderRadius: "1rem",
            border: "2px dashed #dee2e6",
          }}
        >
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>🕯️</div>
          <h3 style={{ color: "#666", marginBottom: "1rem" }}>
            {searchTerm ? "No Matching Aartis Found" : "No Aartis Found"}
          </h3>
          <p style={{ color: "#888", marginBottom: "2rem" }}>
            {searchTerm
              ? "Try adjusting your search term or clear the search to see all aartis."
              : "Start building your aarti collection by adding your first aarti."}
          </p>

          {!searchTerm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="add-aarti-button"
            >
              ➕ Add Your First Aarti
            </button>
          )}
        </div>
      ) : (
        <table className="aarti-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredAartis.map((aarti) => (
              <tr key={aarti.id}>
                <td>
                  <strong>{aarti.name}</strong>
                </td>
                <td>{aarti.description || "No description"}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEditClick(aarti)}
                      className="edit-button"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(aarti.id)}
                      className="delete-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AartiList;
